// Adapters: server chat shape → legacy SPA ChatSession shape, plus a
// hydrator that loads full message lists for sidebar stubs.
//
// Sidebar sessions are summary stubs (messages: []); the turns live in the
// per-chat detail query. Anything that needs message bodies for chats that
// aren't open (export) must hydrate them first.

import type { QueryClient } from '@tanstack/react-query';
import type {
  ChatSession,
  StoredMessage,
  RoutingEvent,
  PlanningEvent,
  ProfileEvent,
  GraphTraversalEvent,
  ScoreThreshold,
  DetectedIOC,
  SourceDoc,
  PipelineStep,
  StepDetail,
  AccumulatedGraph,
} from '../types';
import { getChat, type ChatDetail, type ChatSummary, type TurnRecord } from './feedbackApi';
import { chatKey } from './queries';

export function turnsToStoredMessages(turns: TurnRecord[]): StoredMessage[] {
  const out: StoredMessage[] = [];
  for (const t of turns) {
    out.push({
      id: `${t.turn_id}-user`,
      role: 'user',
      content: t.query,
    });
    out.push({
      id: `${t.turn_id}-assistant`,
      role: 'assistant',
      content: t.answer,
      turnId: t.turn_id,
      sources: (t.sources as SourceDoc[] | null) ?? undefined,
      routingEvent: (t.routing_event as RoutingEvent | null) ?? undefined,
      planningEvent: (t.planning_event as PlanningEvent | null) ?? undefined,
      profileEvent: (t.profile_event as ProfileEvent | null) ?? undefined,
      graphTraversalEvent: (t.graph_traversal as GraphTraversalEvent | null) ?? undefined,
      completedSteps: (t.step_details?.completed_steps as PipelineStep[] | undefined) ?? undefined,
      stepDetails: (t.step_details?.step_details as StepDetail[] | undefined) ?? undefined,
      scoreThreshold: (t.step_details?.score_threshold as ScoreThreshold | null) ?? undefined,
      queryDetectedIocs: (t.detected_iocs?.query as DetectedIOC[] | undefined) ?? undefined,
      responseDetectedIocs: (t.detected_iocs?.response as DetectedIOC[] | undefined) ?? undefined,
    });
  }
  return out;
}

export function summaryToSessionStub(c: ChatSummary): ChatSession {
  // Sidebar-style stub: no .messages (they live in useChat detail).
  // serverChatId === id; serverIsSensitive mirrors the server.
  return {
    id: c.chat_id,
    title: c.title ?? '',
    createdAt: Date.parse(c.created_at) || Date.now(),
    updatedAt: Date.parse(c.updated_at) || Date.now(),
    messages: [],
    folderId: c.folder_id ?? undefined,
    serverChatId: c.chat_id,
    serverIsSensitive: c.is_sensitive,
  };
}

export function detailToSession(c: ChatDetail): ChatSession {
  return {
    id: c.chat_id,
    title: c.title ?? '',
    createdAt: Date.parse(c.created_at) || Date.now(),
    updatedAt: Date.parse(c.updated_at) || Date.now(),
    messages: turnsToStoredMessages(c.turns),
    accumulatedGraph: (c.accumulated_graph as AccumulatedGraph | null) ?? undefined,
    folderId: c.folder_id ?? undefined,
    serverChatId: c.chat_id,
    serverIsSensitive: c.is_sensitive,
    activeRun: c.active_run
      ? { runId: c.active_run.run_id, query: c.active_run.query }
      : undefined,
  };
}

/** Return `sessions` with messages filled in. Local sessions (drafts,
 *  temps) and sessions that already carry messages are passed through;
 *  server stubs are fetched through the query cache (so open chats cost
 *  nothing). A failed fetch keeps the stub rather than failing the batch. */
export async function hydrateSessions(
  qc: QueryClient,
  sessions: ChatSession[],
): Promise<ChatSession[]> {
  return Promise.all(
    sessions.map(async (s) => {
      if (s.messages.length > 0 || !s.serverChatId) return s;
      try {
        const detail = await qc.fetchQuery({
          queryKey: chatKey(s.serverChatId),
          queryFn: () => getChat(s.serverChatId as string),
          staleTime: 10_000,
        });
        return { ...detailToSession(detail), pinned: s.pinned };
      } catch {
        return s;
      }
    }),
  );
}
